import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Link } from '@blueprint/link';
import { SecondaryNavWrapper } from './SecondaryNav/SecondaryNav.styles';
import smallZ from '../../zLogo';

const LinkWrapper = styled('div')(
    (props) => css`
        display: flex;
        flex: 1 0 auto;
        align-items: center;
        cursor: pointer;
        border-bottom: 1px solid transparent;

        &:hover {
            border-bottom-color: ${props.theme.colors[
                'graphic-background-accent'
            ]};
        }

        ${props.theme.mediaQueries.large} {
            border-bottom: none;
            flex-direction: column;

            &:after {
                content: '';
                height: 12px;
                bottom: 0;
                display: block;
            }

            &:hover,
            &:focus {
                ~ ${SecondaryNavWrapper} {
                    margin-top: 0;
                    position: absolute;
                }

                ${Link} {
                    &:after {
                        opacity: 1;
                        height: 2px;
                    }
                }
            }
        }

        ${Link} {
            flex: 1 0 auto;
            pointer-events: none;
            font-size: 14px;
            line-height: 20px;
            padding: 14px 0;
            text-decoration: none;
            border-radius: ${props.theme.borderRadius['4px']};
            color: ${props.theme.colors['primary-dark']};
            position: relative;

            &:focus,
            &:hover {
                text-decoration: none;
                transform: translateY(0);
            }

            :focus {
                box-shadow: inset ${props.theme.focusRing};
            }

            ${props.theme.mediaQueries.large} {
                display: inline-block;
                padding: 14px ${props.theme.spacing['16px']};
                pointer-events: initial;
            }

            &:after {
                content: '';
                position: absolute;
                height: 1px;
                background-color: ${props.theme.colors['primary-brand']};
                opacity: 0;
                left: ${props.theme.spacing['16px']};
                bottom: ${props.theme.spacing['16px']};
                right: ${props.theme.spacing['16px']};
                margin-bottom: -3px;
            }
        }
    `
);

const PrimaryList = styled('ul')(
    (props) => css`
        list-style: none;
        padding: 0;
        margin: 0;

        > li {
            position: relative;
            margin: 0;
            padding: 0;

            ${props.theme.mediaQueries.large} {
                &:focus-within,
                &:hover {
                    ${LinkWrapper} > ${Link} {
                        &:after {
                            opacity: 1;
                        }
                    }
                }

                &:focus-within {
                    ${SecondaryNavWrapper} {
                        margin-top: 0;
                        position: absolute;
                    }
                }
            }
        }
    `
);

const AccordionIcon = styled('div')(
    (props) => css`
        width: ${props.theme.spacing['16px']};
        height: ${props.theme.spacing['16px']};
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23322744' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' d='M7.5 7.5V0h1v7.5H16v1H8.5V16h-1V8.5H0v-1h7.5z' clip-rule='evenodd'/%3E%3C/svg%3E");
        background-size: 16px 16px;
        background-color: transparent;
        background-position: center;
        appearance: none;
        border: 0;
        padding: 0;
        margin-top: ${props.theme.spacing['16px']};
        margin-right: 20px;
        margin-bottom: ${props.theme.spacing['16px']};
        margin-left: 24px;
        pointer-events: none;

        ${props.theme.mediaQueries.large} {
            display: none;
        }
    `
);

const AccordionIconLogo = styled(AccordionIcon)((props) => {
    return css`
        width: 24px;
        height: ${props.theme.spacing['24px']};
        background-size: 24px 24px;
        margin-right: 16px;
        margin-left: 20px;
        background-image: ${smallZ};
    `;
});

const PrimaryListWrapper = styled(PrimaryList)(
    (props) => css`
        background-color: ${props.theme.colors['graphic-background']};
        display: flex;
        flex-flow: column wrap;

        ${props.theme.mediaQueries.large} {
            flex-flow: row nowrap;
        }

        > li {
            header:not([data-is-logged-in]) &[data-is-logged-in='true'] {
                display: none;
            }

            [data-is-logged-in] &[data-is-logged-in='false'] {
                display: none;
            }

            &:hover,
            &:focus {
                &:before {
                    visibility: visible;
                }
            }
        }

        [data-universal-header-accordion-open] {
            ~ ${SecondaryNavWrapper} {
                max-height: 999px;
            }

            ${AccordionIcon} {
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23322744' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' d='M16 8.5H0v-1h16v1z' clip-rule='evenodd'/%3E%3C/svg%3E");
            }
        }
    `
);

const AccountMenu = styled('li')(
    (props) => css`
        [data-is-logged-in] &[data-is-logged-in='false'] {
            display: none;
        }

        ${props.theme.mediaQueries.large} {
            display: none;
        }
        header & {
            ${Link} {
                color: ${props.theme.colors['primary-brand']};
            }
        }
    `
);

const StyledPrimaryNav = styled('nav')(
    css`
        @media (min-width: 1450px) {
            position: absolute;
            left: 146px;
            top: -60px;
            right: 0;
        }
    `
);

export {
    StyledPrimaryNav,
    PrimaryList,
    PrimaryListWrapper,
    LinkWrapper,
    AccordionIcon,
    AccountMenu,
    AccordionIconLogo
};
